/**
 * ViewportGuard Component
 *
 * Prevents rendering when viewport is below minimum size.
 * Shows a friendly message instead.
 *
 * DISABLED by default as of 2026-01-10:
 * Panel resize functionality now works correctly with react-resizable-panels,
 * so the viewport guard is no longer needed. Set enabled={true} to re-enable.
 */
import { useWindowResize } from '@/hooks/useWindowResize';

const MIN_WIDTH = 1024;
const MIN_HEIGHT = 768;

interface ViewportGuardProps {
  children: React.ReactNode;
  /** Enable viewport size checking. Default: false (disabled) */
  enabled?: boolean;
}

export function ViewportGuard({ children, enabled = false }: ViewportGuardProps) {
  const { windowSize } = useWindowResize();

  // Skip check if disabled
  if (!enabled) {
    return <>{children}</>;
  }

  const isTooSmall = windowSize.width < MIN_WIDTH || windowSize.height < MIN_HEIGHT;

  if (isTooSmall) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-vibe-bg text-vibe-text">
        <div className="text-center p-8 max-w-md">
          <div className="text-6xl mb-6">📐</div>
          <h2 className="text-2xl font-semibold mb-3">Window Too Small</h2>
          <p className="text-vibe-text-secondary mb-4">
            Vibe Designer requires a minimum window size of {MIN_WIDTH} × {MIN_HEIGHT} pixels
            for the best experience.
          </p>
          <div className="bg-vibe-bg-secondary rounded-lg p-4 text-sm">
            <div className="flex justify-between mb-2">
              <span className="text-vibe-text-secondary">Current size:</span>
              <span className="font-mono">{windowSize.width} × {windowSize.height}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-vibe-text-secondary">Required:</span>
              <span className="font-mono text-vibe-accent">{MIN_WIDTH} × {MIN_HEIGHT}</span>
            </div>
          </div>
          <p className="text-xs text-vibe-text-secondary mt-4">
            Please resize your browser window or use a larger display.
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
